import React from 'react';
import styled from 'styled-components';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { Grid, Box } from '@mui/material';
import { academicRefereeTypeOptions } from './options';
import { type AcademicRefBoxInfo, type InputTextProps } from './types';
import { PAGE_TEXT } from '../lang';
import { PAGE_TEXT as COMPONENT_TEXT } from '../../../../Signup/components';
import type { AcademicRefFormData } from '../../../graphql/ApplicationForm/academicReferee';
import { TextField, Select, PhoneInputField } from '../../../../../components';
import { handleReadySubmit } from '../../../../../utils';

const pageText = PAGE_TEXT.OTHER_INFORMATION.ACADEMIC_REFEREE;
const academicRefFormText: AcademicRefBoxInfo = {
  subTitle: pageText.TITLE,
  initialForm: [
    {
      label: pageText.REFEREE_TITLE,
      type: 'select',
      name: 'title',
      options: academicRefereeTypeOptions,
    },
    {
      label: pageText.SURNAME,
      type: 'text',
      name: 'surname',
    },
    {
      label: pageText.GIVEN_NAME,
      type: 'text',
      name: 'givenName',
    },
    {
      label: pageText.INSTITUTION,
      type: 'textarea',
      name: 'institution',
    },
    {
      label: pageText.POSITION,
      type: 'text',
      name: 'position',
    },
    {
      label: pageText.RELATIONSHIP,
      type: 'text',
      name: 'relationship',
    },
    {
      label: pageText.EMAIL,
      type: 'email',
      name: 'email',
      placeHolder: 'xxx@polyu.edu.hk',
    },
    {
      label: pageText.PHONE_NUMBER,
      type: 'text',
      name: 'phoneNumber',
    },
    {
      label: pageText.FAX_NUMBER,
      type: 'text',
      name: 'faxNumber',
    },
  ],
};

type AcademicBoxProps = {
  initialAcademicRefValues: AcademicRefFormData;
  enableEdit: boolean;
  academicRefIndex: number;
  actionArea?: React.ReactNode;
  handleSubmit: (values: AcademicRefFormData) => Promise<void>;
};

export const AcademicRefBox: React.FC<AcademicBoxProps> = (
  props: AcademicBoxProps,
) => {
  const { initialForm } = academicRefFormText;
  const {
    initialAcademicRefValues,
    academicRefIndex,
    enableEdit,
    actionArea,
    handleSubmit,
  } = props;

  const formik = useFormik<AcademicRefFormData>({
    initialValues: initialAcademicRefValues,
    validationSchema: object({
      title: string().required('Required'),
      surname: string().required('Required'),
      givenName: string().required('Required'),
      institution: string().required('Required'),
      position: string().required('Required'),
      relationship: string().required('Required'),
      email: string().email('Invalid email format').required('Required'),
      phoneNumber: string()
        .required('Required')
        .min(5, COMPONENT_TEXT.REQUIRED)
        .required(COMPONENT_TEXT.REQUIRED),
    }),
    onSubmit: async (values): Promise<void> => {
      await handleSubmit({ ...values });
    },
  });

  const handleUpdateTelephone = async (value: string): Promise<void> => {
    const phoneNumber = '+' + value.replace(/\D/g, '');
    await formik.setFieldValue('phoneNumber', phoneNumber);
  };

  return (
    <Container>
      <ContentBox>
        <form onSubmit={handleReadySubmit(formik, academicRefIndex)} noValidate>
          <Box sx={{ flexGrow: 1 }}>
            <Grid container columnSpacing={4} rowSpacing={2}>
              {initialForm.map((item: InputTextProps, index: number) => {
                const { name, type, label, options } = item;
                return (
                  <Grid
                    key={index}
                    size={{
                      md: type === 'textarea' ? 12 : 6,
                    }}
                  >
                    {options ? (
                      <Select
                        className="w-full"
                        disabled={!enableEdit}
                        type={type}
                        required
                        label={label}
                        name={name}
                        options={options}
                        value={formik.values[name]}
                        InputLabelProps={{ shrink: true }}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched[name] && Boolean(formik.errors[name])
                        }
                        helperText={formik.touched[name] && formik.errors[name]}
                      />
                    ) : label === pageText.PHONE_NUMBER ? (
                      <PhoneInputField
                        className="w-full"
                        defaultCountry={'HK'}
                        disabled={!enableEdit}
                        label={label}
                        value={formik.values[name]}
                        required={true}
                        name={name}
                        autoFocus={false}
                        helperText={formik.errors[name]}
                        onChange={handleUpdateTelephone}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched[name] && Boolean(formik.errors[name])
                        }
                      />
                    ) : (
                      <TextField
                        className="w-full"
                        disabled={!enableEdit}
                        type={type}
                        required={label !== pageText.FAX_NUMBER}
                        label={label}
                        name={name}
                        value={formik.values[name]}
                        InputLabelProps={{ shrink: true }}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder={item.placeHolder}
                        error={
                          formik.touched[name] && Boolean(formik.errors[name])
                        }
                        helperText={formik.touched[name] && formik.errors[name]}
                      />
                    )}
                  </Grid>
                );
              })}
            </Grid>
            {enableEdit && actionArea}
          </Box>
        </form>
      </ContentBox>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;

  .MuiSwitch-root {
    margin: 1rem;
  }

  form {
    padding-top: 0.5rem;
    margin-bottom: 2rem;
    width: 100%;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }

  .buttons-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    margin-top: 1rem;
    button {
      margin: 0 1rem;
    }
  }
`;

const ContentBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;

    width: calc(100%);
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .input-area {
    display: flex;
    flex-direction: column;
  }
`;

export const HelperText = styled.div`
  padding: 0.5rem 1rem 1rem 1rem;
  display: flex;
  flex-direction: row;
  align-items: center;

  svg {
    color: #951d1d;
  }

  p {
    margin-left: 0.5rem;
    font-style: italic;
    color: #951d1d;
  }
`;
